import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { useState, useCallback, useEffect, useRef } from "react";
import { Control, Controller, RegisterOptions } from "react-hook-form";
import { IGooglePickerItem } from "../Integrations/GoogleDrivePicker";
import { useSpotUser } from "@/utils/spotUser";
import { Trash2 } from "lucide-react";

import { useRemoteData } from "@/utils/useRemoteData";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { NoxusFormLabel } from "./NoxusFormLabel";
import {
  CustomOneDrivePicker,
  FilePickerServiceName,
} from "./CustomOneDrivePicker";
import { cn } from "@/lib/utils";
import { Button } from "../shadcn/button";
import { NoxusItem } from "../UI/NoxusItem";
import { getFileImage } from "@/utils/generic";

interface IOneDriveSDK {
  open: (options: Record<string, unknown>) => void;
}

declare global {
  interface Window {
    OneDrive: IOneDriveSDK;
  }
}

export interface IOneDrivePickerItem extends IGooglePickerItem {
  content_type: string | null;
  url: string | null | undefined;
  drive_id?: string;
  site_name?: string;
}

interface IOneDrivePickerField {
  name: string;
  label: string;
  canMultiselect?: boolean;
  canSelectDirectories?: boolean;
  allowedFiles?: string;
  control: Control<any>;
  rules?: RegisterOptions;
  defaultValue?: IOneDrivePickerItem[];
  isHideLink?: boolean;
  setIncludeFolders?: boolean;
  baseUrl?: string;
  serviceName?: string;
  isDisabled?: boolean;
  credentialId?: string;
}

interface IOneDrivePicker {
  selectedItems: IOneDrivePickerItem[];
  setSelectedItems: (items: IOneDrivePickerItem[]) => any;
  canSelectDirectories?: boolean;
  canMultiselect?: boolean;
  isLoading?: boolean;
  allowedFiles?: string;
  label: string;
  isHideLink?: boolean;
  setIncludeFolders?: boolean;
  baseUrl?: string;
  serviceName?: string;
  onError?: (error: unknown) => void;
  name?: string;
  isDisabled?: boolean;
  credentialId?: string;
}

const FilePickerComponent = (props: IOneDrivePicker) => {
  const [isError, setIsError] = useState(false);
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const isDisabled = props.isDisabled;

  const handleFileClick = useCallback((file: IOneDrivePickerItem) => {
    const win = window.open(file.url || "", "_blank");
    win?.focus();
  }, []);

  const handleCustomPickerSelect = (items: IOneDrivePickerItem[]) => {
    if (isDisabled) return;
    setIsError(false);
    props.setSelectedItems(items);
  };

  return (
    <>
      {props.label && <NoxusFormLabel label={props.label} name={props.name} />}
      <CustomOneDrivePicker
        onSelect={handleCustomPickerSelect}
        canMultiselect={props.canMultiselect}
        canSelectDirectories={props.canSelectDirectories}
        selectedItems={props.selectedItems}
        serviceName={(props.serviceName as FilePickerServiceName) || "OneDrive"}
        isDisabled={isDisabled}
        credentialId={props.credentialId}
      />

      {isError && (
        <div className="mt-4">
          <MicrosoftPickerErrorBanner />
        </div>
      )}
      {props.selectedItems.length > 0 && (
        <div className="flex items-center justify-between mt-6 max-w-600px">
          <div className="flex flex-col w-full gap-2 items-stretch max-h-60 overflow-y-auto">
            {props.selectedItems.map((item) => (
              <div
                key={item.id}
                onClick={() => handleFileClick(item)}
                className={cn(
                  "w-full",
                  isLoadingFile
                    ? "opacity-60 cursor-not-allowed"
                    : "cursor-pointer opacity-100"
                )}
              >
                <NoxusItem
                  icon={
                    item.content_type === "folder"
                      ? getFileImage("folder")
                      : item.image || getFileImage(item.content_type)
                  }
                  iconAlt={item.name}
                  title={item.name}
                  iconClassName="bg-white"
                  className="bg-background2"
                  actions={
                    <Button
                      aria-label="Remove"
                      variant="ghost"
                      size="iconsm"
                      disabled={isLoadingFile}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        props.setSelectedItems(
                          props.selectedItems.filter((i) => i.id !== item.id)
                        );
                      }}
                    >
                      <Trash2 strokeWidth={1.33} />
                    </Button>
                  }
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export const OneDrivePickerField = ({
  label,
  name,
  canMultiselect = false,
  canSelectDirectories = false,
  allowedFiles,
  control,
  rules,
  defaultValue,
  isHideLink,
  setIncludeFolders,
  baseUrl,
  serviceName,
  isDisabled,
  credentialId,
}: IOneDrivePickerField) => {
  // Track the previous credentialId so we can detect actual changes
  const prevCredentialIdRef = useRef(credentialId);
  const clearFieldRef = useRef<((v: IOneDrivePickerItem[]) => void) | null>(
    null
  );

  useEffect(() => {
    if (
      prevCredentialIdRef.current != null &&
      credentialId != null &&
      prevCredentialIdRef.current !== credentialId
    ) {
      clearFieldRef.current?.([]);
    }
    prevCredentialIdRef.current = credentialId;
  }, [credentialId]);

  const onError = useCallback((error: unknown) => {
    console.error("OneDrive picker error: ", error);
  }, []);

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      defaultValue={defaultValue}
      render={({ field }) => {
        clearFieldRef.current = field.onChange;
        return (
          <FilePickerComponent
            label={label}
            selectedItems={(field.value as IOneDrivePickerItem[]) ?? []}
            setSelectedItems={(items) => field.onChange(items)}
            canMultiselect={canMultiselect}
            canSelectDirectories={canSelectDirectories}
            allowedFiles={allowedFiles}
            isHideLink={isHideLink}
            setIncludeFolders={setIncludeFolders}
            baseUrl={baseUrl}
            serviceName={serviceName}
            onError={onError}
            name={name}
            isDisabled={isDisabled}
            credentialId={credentialId}
          />
        );
      }}
    />
  );
};

const MicrosoftPickerErrorBanner = () => {
  const { spotGroup } = useSpotUser();
  const { data, isLoading } = useRemoteData(
    DATA_KEYS.AZURE_INTEGRATION_NANGO_USERNAME,
    {
      path: { group_id: spotGroup.id },
    }
  );

  return (
    <NoxusAlert
      look="banner"
      variant="destructive"
      title={
        <div className="flex items-center gap-2">
          <p className="text-sm">
            Please ensure you are{" "}
            <strong>
              logged into your browser with{" "}
              {data?.mail ? (
                <u>{data.mail as string}</u>
              ) : (
                "the same Microsoft account that you connected to Noxus"
              )}
            </strong>
            . If you are already using the correct account and still
            experiencing issues, please reach out to our support team through
            Pylon.
          </p>
        </div>
      }
    />
  );
};
